import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environments/environment';
import { EmailInsightDto, ProspectSummary } from '../models/prospect';
import { ActionItemSummary, SignalSummary, TimerSessionDto, WaitingOnSummary } from '../models/pipeline';
import { ProspectsService } from './prospects.service';
import { InsightsService } from './insights.service';
import { PipelineService } from './pipeline.service';
import { TimerService } from './timer.service';

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly msal = inject(MsalService);
  private readonly prospects = inject(ProspectsService);
  private readonly insights = inject(InsightsService);
  private readonly pipeline = inject(PipelineService);
  private readonly timer = inject(TimerService);

  private connection?: signalR.HubConnection;

  // Promises of any in-flight start() / stop() so concurrent calls coalesce
  // rather than racing two parallel connections. The earlier `if (this.connection)`
  // guard alone wasn't enough — `connection` is assigned BEFORE `connection.start()`
  // resolves, so a second start() that fired during the handshake could see a
  // half-built connection and skip silently.
  private startInFlight?: Promise<void>;
  private stopInFlight?: Promise<void>;

  // Tracks consecutive failed token acquisitions so we can stop the auto-reconnect
  // loop instead of burning CPU re-trying with empty tokens forever.
  private consecutiveTokenFailures = 0;
  private static readonly MAX_TOKEN_FAILURES = 3;

  async start(): Promise<void> {
    // Wait for any prior stop() to complete first so we don't race two connections
    // (sign-out → sign-in toggle could otherwise interleave).
    if (this.stopInFlight) {
      try { await this.stopInFlight; } catch { /* tolerate prior-stop errors */ }
    }
    if (this.connection) return;
    if (this.startInFlight) { await this.startInFlight; return; }

    this.startInFlight = this.startInternal();
    try { await this.startInFlight; }
    finally { this.startInFlight = undefined; }
  }

  private async startInternal(): Promise<void> {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(environment.hubUrl, {
        accessTokenFactory: async () => {
          const account = this.msal.instance.getActiveAccount();
          if (!account) {
            await this.abortAfterRepeatedTokenFailures();
            return '';
          }
          try {
            const token = await this.msal.instance.acquireTokenSilent({
              account,
              scopes: [...environment.msal.apiScopes],
            });
            this.consecutiveTokenFailures = 0;
            return token.accessToken;
          } catch (err) {
            console.warn('SignalR token acquisition failed:', err);
            await this.abortAfterRepeatedTokenFailures();
            return '';
          }
        },
      })
      .withAutomaticReconnect({
        // Never give up. The default array policy stopped after ~42s offline,
        // which silently killed realtime for the rest of the session (laptop
        // sleep, network blip, API restart). Ladder up to a steady 60s ± jitter.
        nextRetryDelayInMilliseconds: ctx => {
          const ladder = [0, 2_000, 10_000, 30_000];
          return ctx.previousRetryCount < ladder.length
            ? ladder[ctx.previousRetryCount]
            : 60_000 + Math.floor(Math.random() * 5_000);
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Events that fired while we were disconnected are gone — SignalR has no
    // replay. Re-pull the caches the hub normally keeps fresh, using whatever
    // scope each surface last requested.
    conn.onreconnected(() => {
      console.info('[realtime] Reconnected — resyncing caches.');
      void this.resyncAfterReconnect();
    });

    conn.on('prospectChanged', (s: ProspectSummary) => this.prospects.applyChange(s));
    conn.on('prospectDeleted', (id: string) => this.prospects.applyDelete(id));
    conn.on('emailInsightRaised', (i: EmailInsightDto) => this.insights.applyRealtime(i));
    conn.on('emailInsightDismissed', (id: string) => this.insights.applyDismissed(id));
    conn.on('agentToolEvent', () => { /* hook for streaming UI */ });

    // Phase 2: pipeline events
    conn.on('signalCaptured', (s: SignalSummary) => this.pipeline.applySignalChange(s));
    conn.on('signalChanged', (s: SignalSummary) => this.pipeline.applySignalChange(s));
    conn.on('actionChanged', (a: ActionItemSummary) => this.pipeline.applyActionChange(a));
    conn.on('actionDeleted', (id: string) => this.pipeline.applyActionDeleted(id));
    conn.on('waitingOnChanged', (w: WaitingOnSummary) => this.pipeline.applyWaitingChange(w));

    // Phase 1 SPEC v2 §6.8 Step 3 — bottom-bar timer state
    conn.on('timerStarted', (s: TimerSessionDto) => this.timer.applyStarted(s));
    conn.on('timerStopped', (s: TimerSessionDto) => this.timer.applyStopped(s));

    await conn.start();
    // Only assign once .start() has resolved so callers that observe `connection`
    // see a fully-handshaken object.
    this.connection = conn;
  }

  async stop(): Promise<void> {
    if (this.startInFlight) {
      try { await this.startInFlight; } catch { /* swallow */ }
    }
    if (!this.connection) return;
    if (this.stopInFlight) { await this.stopInFlight; return; }

    const c = this.connection;
    this.stopInFlight = c.stop().finally(() => {
      this.connection = undefined;
      this.consecutiveTokenFailures = 0;
      this.stopInFlight = undefined;
    });
    await this.stopInFlight;
  }

  /**
   * After a reconnect, refresh every cache the hub feeds. Pipeline replays the
   * last page-requested filters; the rest are cheap global loads. Failures are
   * tolerated — the next user interaction reloads anyway.
   */
  private async resyncAfterReconnect(): Promise<void> {
    await Promise.allSettled([
      this.prospects.loadAll(),
      this.insights.load(),
      this.pipeline.resync(),
      this.timer.loadCurrent(),
    ]);
  }

  private async abortAfterRepeatedTokenFailures(): Promise<void> {
    this.consecutiveTokenFailures++;
    if (this.consecutiveTokenFailures >= RealtimeService.MAX_TOKEN_FAILURES) {
      console.warn(
        `[realtime] Stopping hub after ${this.consecutiveTokenFailures} consecutive token-acquisition failures. ` +
        `Sign in again to re-establish the realtime connection.`);
      try { await this.connection?.stop(); } catch { /* ignore */ }
      this.connection = undefined;
    }
  }
}
